import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import {
  getResultsByEventId,
  createResultsEventFromEvent,
  updateResultsEventLinks,
  deleteResultsEvent,
} from "../../../services/resultsEventServices";
import { FaExternalLinkAlt, FaTimes, FaSave, FaTrash, FaPlus } from "react-icons/fa";

/* Utils */
const toDateSafe = (v) => {
  const d = v ? new Date(v) : null;
  return Number.isNaN(d?.getTime?.()) ? null : d;
};

const fmt = (v) => {
  const d = toDateSafe(v);
  if (!d) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const FIELDS = [
  { name: "rapport_url", label: "Rapport (PDF)" },
  { name: "tableaux_url", label: "Tableaux (PDF)" },
  { name: "selection_url", label: "Sélection (PDF)" },
  { name: "photospodium_url", label: "Photos podium (URL)" },
];

export default function ResultsEventModal({ isOpen, onClose, event }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [result, setResult] = useState(null);

  const [links, setLinks] = useState({
    rapport_url: "",
    tableaux_url: "",
    selection_url: "",
    photospodium_url: "",
  });

  const titleId = useMemo(() => "results-event-modal-title", []);
  const descId = useMemo(() => "results-event-modal-desc", []);

  const loadExisting = async (eventId) => {
    setLoading(true);
    setError("");

    try {
      const arr = await getResultsByEventId(eventId);
      const rows = Array.isArray(arr) ? arr : [];

      rows.sort(
        (a, b) =>
          (toDateSafe(b.start)?.getTime?.() ?? 0) -
          (toDateSafe(a.start)?.getTime?.() ?? 0)
      );

      const first = rows[0] || null;

      setResult(first);
      setLinks({
        rapport_url: first?.rapport_url || "",
        tableaux_url: first?.tableaux_url || "",
        selection_url: first?.selection_url || "",
        photospodium_url: first?.photospodium_url || "",
      });
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les résultats existants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && event?.id) {
      loadExisting(event.id);
    } else {
      setResult(null);
      setLinks({
        rapport_url: "",
        tableaux_url: "",
        selection_url: "",
        photospodium_url: "",
      });
      setError("");
    }
  }, [isOpen, event?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLinks((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async () => {
    if (!event?.id) return;

    setSaving(true);
    setError("");

    try {
      const created = await createResultsEventFromEvent(event.id, { ...links });
      setResult(created || null);
    } catch (e) {
      console.error(e);
      setError("Échec de la création du résultat.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!result?.id) return;

    setSaving(true);
    setError("");

    try {
      const updated = await updateResultsEventLinks(result.id, { ...links });
      setResult(updated || result);
    } catch (e) {
      console.error(e);
      setError("Échec de la mise à jour des liens.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!result?.id) return;
    if (!window.confirm("Supprimer ce résultat ?")) return;

    setSaving(true);
    setError("");

    try {
      await deleteResultsEvent(result.id);
      setResult(null);
      setLinks({
        rapport_url: "",
        tableaux_url: "",
        selection_url: "",
        photospodium_url: "",
      });
    } catch (e) {
      console.error(e);
      setError("Échec de la suppression.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById={titleId}
      describedById={descId}
      initialFocusSelector='input[name="rapport_url"]'
    >
      <ModalHeader
        id={titleId}
        title={`Résultats — ${event?.titre || "Événement"}`}
        subtitle={
          event
            ? `${fmt(event.start)} • ${event.type_event || "—"} • ${
                event.level_event || "—"
              } • ${event.lieu_event || "—"}`
            : "—"
        }
        onClose={onClose}
      />

      <ModalBody id={descId}>
        {loading ? (
          <div className="py-10 text-center text-slate-500">Chargement…</div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-500">Titre</label>
                  <div className="mt-1 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                    {event?.titre || "—"}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-500">Organisateur</label>
                  <div className="mt-1 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                    {event?.organisateur || "—"}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-500">Catégories</label>
                  <div className="mt-1 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                    {event?.cate_event || "—"}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-500">Type / Niveau</label>
                  <div className="mt-1 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-800 dark:bg-slate-800 dark:text-slate-100">
                    {(event?.type_event || "—") + " / " + (event?.level_event || "—")}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                  Liens associés aux résultats
                </h4>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Vous pouvez renseigner un ou plusieurs liens PDF / URL pour cet événement.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {FIELDS.map((f) => (
                  <div key={f.name}>
                    <label className="mb-1 block text-xs text-slate-600 dark:text-slate-300">
                      {f.label}
                    </label>

                    <div className="flex gap-2">
                      <input
                        type="url"
                        name={f.name}
                        value={links[f.name]}
                        onChange={handleChange}
                        placeholder="https://…"
                        className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      />

                      {links[f.name] ? (
                        <a
                          href={links[f.name]}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Ouvrir le lien"
                          className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-3 py-2 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FaExternalLinkAlt />
                        </a>
                      ) : (
                        <button
                          type="button"
                          disabled
                          title="Aucun lien"
                          className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-400 dark:border-slate-700 dark:bg-slate-800"
                        >
                          —
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            ) : null}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {result ? (
          <>
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="inline-flex h-11 items-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
            >
              <FaTrash />
              Supprimer
            </button>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={handleUpdate}
              disabled={saving}
              className="inline-flex h-11 items-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50"
            >
              <FaSave />
              {saving ? "Enregistrement…" : "Modifier"}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={handleCreate}
              disabled={saving || loading || !event?.id}
              className="inline-flex h-11 items-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
            >
              <FaPlus />
              {saving ? "Création…" : "Créer"}
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}